import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  CalculatorIcon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Modal } from "shared/components/Modal";
import { ApplicationMetricData } from "api/applications";
import { METRIC_MODAL_MAP } from "../../constants";
import { MetricCalculationColumn } from "./MetricCalculationColumn";
import { cx } from "shared/utils";

const shouldShowFormula = (formula?: string) => {
  if (!formula || formula === "nil") return false;

  const [leftSide, rightSide] = formula.split("=");
  const isFormulaSymmetric = leftSide?.trim() === rightSide?.trim();

  return !isFormulaSymmetric;
};

export const MetricCalculationModal = ({
  metricData,
  onClose
}: {
  metricData: ApplicationMetricData | null;
  onClose: () => void;
}) => {
  if (!metricData) return null;
  const config = METRIC_MODAL_MAP[metricData.metric];
  if (!config) return null;

  const showFormula = shouldShowFormula(metricData.formula);

  return (
    <Modal
      opened={Boolean(metricData)}
      onClose={onClose}
      panelClassName="min-w-[560px] max-w-2xl bg-background-900 shadow-lg"
    >
      <header className="flex items-center bg-background-1000 border-b border-alpha-100 rounded-t-lg py-4 px-6">
        <div className="h-8 w-8 rounded-sm border border-alpha-100 flex items-center">
          <HugeiconsIcon
            icon={CalculatorIcon}
            size={16}
            className="text-content-700 m-auto"
          />
        </div>
        <p className="font-bold text-content-1000 ml-2.5">{config.title}</p>
        <HugeiconsIcon
          icon={Cancel01Icon}
          onClick={onClose}
          className="ml-auto text-content-500 cursor-pointer"
        />
      </header>

      <Show if={Boolean(metricData.warnings?.length) && config.warningText}>
        <div className="px-6 py-4 bg-warning-50 flex gap-2 border-b border-alpha-80">
          <HugeiconsIcon
            icon={AlertCircleIcon}
            size={20}
            className="text-warning-500 -mb-0.5"
          />
          <p className="text-sm font-medium text-warning-700 mr-2">
            {config.warningText}
          </p>
        </div>
      </Show>

      <div className="py-4 px-7">
        <p className="text-sm text-content-400 mt-1 mb-4">
          {config.detailText}
        </p>
        <div className="grid bg-background-1000 border border-alpha-100 shadow-xs rounded-lg py-1 mb-3">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${config.columns.length}, auto)`
            }}
          >
            {metricData.applicants.map((applicant, i) => {
              const isLastApplicant = i === metricData.applicants.length - 1;
              return (
                <div
                  key={applicant.applicant_id}
                  className={cx(
                    "grid grid-cols-subgrid col-span-full mx-6 py-4 border-alpha-40 items-center",
                    isLastApplicant && !showFormula ? "" : "border-b"
                  )}
                >
                  {config.columns.map((column, j) => {
                    const isFinalItem = j === config.columns.length - 1;
                    return (
                      <div
                        key={`${applicant.applicant_id}-${column}`}
                        className={isFinalItem ? "ml-auto" : "mr-14"}
                      >
                        <MetricCalculationColumn
                          columnType={column}
                          applicantData={applicant}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <Show if={showFormula}>
            <div className="text-sm font-medium text-content-700 bg-alpha-40 rounded-full w-fit mx-6 my-4 py-0.5 px-3">
              {metricData.formula}
            </div>
          </Show>
        </div>
      </div>
    </Modal>
  );
};
